import React, { useEffect } from 'react';
import { BadgeCheck, Clock, Loader2, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { RootState } from '@/store';
import { getVenueMonetisation } from '@/store/thunkMidddleware/monetisationMiddleware';
import { usePlanSwitcher } from '@/hooks/usePlanSwitcher';
import {
  VenueMonetisation,
  formatPaise,
  isAwaitingPayment,
  isPlanChangeLocked,
  isZeroCommission,
  planLabel,
} from '@/types/monetisationTypes';

/**
 * The selected venue's plan, in the page header.
 *
 * It belongs with the venue-wide controls — the bookings switch, the venue's
 * settings — and not in the picker below, where it competed for width with the
 * courts and cost the board a row. One line is all it gets: the plan, and the
 * one thing about it that is either a price or a problem. The detail is a click
 * away in the same dialog the sidebar opens, so there is still exactly one place
 * a plan actually changes.
 */

const CYCLE_SHORT: Record<string, string> = { monthly: '/mo', yearly: '/yr' };

interface VenuePlanPillProps {
  venueId?: string;
  venueName?: string;
}

const VenuePlanPill: React.FC<VenuePlanPillProps> = ({ venueId, venueName }) => {
  const dispatch = useAppDispatch();
  const { venuePlans, venuePlanResponse } = useAppSelector((state: RootState) => state.monetisation);
  const { openSwitcher, verifying, modals } = usePlanSwitcher(() => venueName);

  const current: VenueMonetisation | undefined = venueId ? venuePlans[venueId] : undefined;

  // Reading a plan mints the record server-side, so it is only read once per
  // venue here; the switcher refreshes the store itself after a change.
  useEffect(() => {
    if (!venueId || venuePlans[venueId]) return;
    dispatch(getVenueMonetisation({ venueId }));
  }, [venueId, venuePlans, dispatch]);

  if (!venueId) return null;

  const loading = venuePlanResponse?.isLoading && !current;
  const onSubscription = current?.plan === 'SUBSCRIPTION';
  const awaitingPayment = isAwaitingPayment(current);
  const pending = awaitingPayment ? undefined : current?.pendingPlanChange;
  // A granted plan (founding partner) cannot be switched at all, so the pill
  // opens the same dialog read-only rather than inviting a switch.
  const locked = isPlanChangeLocked(current);
  const zeroCommission = isZeroCommission(current?.plan);
  // A granted plan has no mandate to break — only the paid subscription does.
  const mandateBroken =
    onSubscription && !locked && current?.mandate?.status && current.mandate.status !== 'ACTIVE';
  const unsettled = Boolean(awaitingPayment || pending || mandateBroken);

  const daysRemaining =
    current?.foundingPartner?.daysRemaining ?? current?.switchEligibility?.daysRemaining;

  // One line, so a problem takes the place of the price: a vendor who owes a
  // payment does not need reminding what the plan costs.
  const detail = awaitingPayment
    ? 'Payment pending'
    : pending
      ? `Switching to ${pending.toPlan === 'COMMISSION' ? 'pay per booking' : 'zero commission'}`
      : mandateBroken
        ? 'Auto-debit needs re-authorising'
        : locked
          ? typeof daysRemaining === 'number'
            ? `0% · ${daysRemaining} days left`
            : '0% commission'
          : onSubscription
            ? `${formatPaise(current?.payablePaise)}${CYCLE_SHORT[current?.billingCycle ?? ''] ?? ''}`
            : 'Per booking';

  const Icon = awaitingPayment || pending ? Clock : mandateBroken ? TriangleAlert : BadgeCheck;

  return (
    <>
      <button
        type="button"
        onClick={() => openSwitcher(venueId, venueName)}
        disabled={loading}
        title={
          locked
            ? `${planLabel(current?.plan)} — view plan`
            : `${planLabel(current?.plan)} — change plan`
        }
        className={cn(
          'inline-flex items-center gap-2 h-10 px-3.5 max-w-[320px] rounded-flup-sm border transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-2',
          unsettled
            ? 'bg-flup-warning-soft border-flup-warning-soft hover:brightness-95'
            : 'bg-flup-surface border-flup-hairline hover:bg-flup-surface-alt',
          loading && 'opacity-60 pointer-events-none',
        )}
      >
        {verifying ? (
          <Loader2 className="w-4 h-4 shrink-0 animate-spin text-flup-brand" aria-hidden />
        ) : (
          <Icon
            className={cn(
              'w-4 h-4 shrink-0',
              unsettled
                ? 'text-flup-warning'
                : locked
                  ? 'text-flup-brand'
                  : zeroCommission
                    ? 'text-flup-green'
                    : 'text-flup-ink-3',
            )}
            aria-hidden
          />
        )}

        {loading ? (
          <Text variant="caption" tone="tertiary" as="span">
            Loading plan…
          </Text>
        ) : !current ? (
          <Text variant="caption" tone="tertiary" as="span">
            Plan unavailable
          </Text>
        ) : (
          <span className="flex items-baseline gap-1.5 min-w-0">
            <Text variant="labelSemi" as="span" lines={1}>
              {planLabel(current.plan)}
            </Text>
            <Text variant="micro" tone="tertiary" as="span" lines={1}>
              · {detail}
            </Text>
          </span>
        )}
      </button>

      {modals}
    </>
  );
};

export default VenuePlanPill;
